import { AppState, AppStateStatus, Platform } from 'react-native';
import {
  initialize,
  warmup,
  initiatePayment,
  abortTransaction as sdkAbortTransaction,
  setDisplaySettings as sdkSetDisplaySettings,
  PaymentResult,
  DisplaySettings,
} from '@oolio-group/pay-embedded';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { logCdsEvent } from '../actionLogging/cdsAudit';
import Sentry from './Sentry/Sentry';
import {
  ensureDeviceVirtualTerminal,
  getStoredVirtualTerminal,
  type StoredVirtualTerminal,
  type VirtualTerminalTrigger,
} from './tap2PayVirtualTerminal';

interface InitConfig {
  store: string;
  merchant: string;
  accessToken: string;
  userId: string;
  terminalId: string;
}

interface SetupConfig extends InitConfig {
  deviceId: string;
  displaySettings?: DisplaySettings | null;
  onInitError?: (error: unknown) => void;
  onVirtualTerminalError?: (error: unknown) => void;
  onVirtualTerminalReady?: (terminal: StoredVirtualTerminal) => void;
  onInitSuccess?: (durationMs: number) => void;
}

const PAYMENT_FOREGROUND_GRACE_MS = 10_000;
const VIRTUAL_TERMINAL_RETRY_MS = 60_000;

class Tap2PayEmbeddedManager {
  private initialized = false;
  private initializing = false;
  private paymentInProgress = false;
  private lastConfig: InitConfig | null = null;
  private lastSetupKey: string | null = null;
  private virtualTerminalSetup: SetupConfig | null = null;
  private virtualTerminalAttemptAt = 0;
  private paymentEndedAt = 0;
  private warmupInFlight: Promise<void> | null = null;
  private appState: AppStateStatus = AppState.currentState;
  private foregroundSubscription: ReturnType<
    typeof AppState.addEventListener
  > | null = null;

  setup(config: SetupConfig): void {
    if (Platform.OS !== 'android') return;

    const setupKey = JSON.stringify({
      store: config.store,
      merchant: config.merchant,
      accessToken: config.accessToken,
      userId: config.userId,
      terminalId: config.terminalId,
      deviceId: config.deviceId,
      displaySettings: config.displaySettings ?? null,
    });

    if (setupKey === this.lastSetupKey) return;
    this.lastSetupKey = setupKey;

    this.watchForeground();
    void this.runSetup(config);
  }

  private async runSetup(config: SetupConfig): Promise<void> {
    this.virtualTerminalSetup = config;

    const startedAt = Date.now();
    try {
      const didInitialize = await this.initialize({
        store: config.store,
        merchant: config.merchant,
        accessToken: config.accessToken,
        userId: config.userId,
        terminalId: config.terminalId,
      });
      if (didInitialize) {
        config.onInitSuccess?.(Date.now() - startedAt);
      }
    } catch (error) {
      // Let the next session update retry.
      this.lastSetupKey = null;
      config.onInitError?.(error);
      return;
    }

    if (this.initialized) this.requestVirtualTerminal('setup');

    if (config.displaySettings) {
      try {
        await this.setDisplaySettings(config.displaySettings);
      } catch (error: unknown) {
        logCdsEvent({
          actionType: 'T2P_DISPLAY_SETTINGS_FAILED',
          tag: AuditLogTags.PAYMENT,
          phrase: 'card reader display settings failed to apply',
          level: 'warn',
          context: {
            errorMessage:
              error instanceof Error ? error.message : String(error),
          },
        });
      }
    }

    await this.warmup();
  }

  private requestVirtualTerminal(trigger: VirtualTerminalTrigger): void {
    const config = this.virtualTerminalSetup;
    if (!config) return;
    this.virtualTerminalAttemptAt = Date.now();
    ensureDeviceVirtualTerminal({
      deviceId: config.deviceId,
      merchant: config.merchant,
      store: config.store,
      accessToken: config.accessToken,
      userId: config.userId,
      trigger,
    })
      .then(terminal => config.onVirtualTerminalReady?.(terminal))
      .catch(error => config.onVirtualTerminalError?.(error));
  }

  private async savedVirtualTerminalId(): Promise<string | undefined> {
    const config = this.virtualTerminalSetup;
    if (!config) return undefined;
    const saved = await getStoredVirtualTerminal({
      deviceId: config.deviceId,
      merchant: config.merchant,
      store: config.store,
    }).catch(() => undefined);
    if (
      !saved &&
      Date.now() - this.virtualTerminalAttemptAt > VIRTUAL_TERMINAL_RETRY_MS
    ) {
      this.requestVirtualTerminal('payment');
    }
    return saved?.terminalId;
  }

  private watchForeground(): void {
    if (this.foregroundSubscription) return;

    this.appState = AppState.currentState;
    this.foregroundSubscription = AppState.addEventListener(
      'change',
      nextAppState => {
        const returnedToFront =
          !!this.appState?.match(/inactive|background/) &&
          nextAppState === 'active';
        const causedByPayment =
          this.paymentInProgress ||
          Date.now() - this.paymentEndedAt < PAYMENT_FOREGROUND_GRACE_MS;

        if (returnedToFront && !causedByPayment) {
          void this.warmup();
        }
        this.appState = nextAppState;
      },
    );
  }

  async initialize(config: InitConfig): Promise<boolean> {
    if (Platform.OS !== 'android') return false;
    if (this.initializing) return false;
    if (
      this.initialized &&
      this.lastConfig &&
      this.lastConfig.store === config.store &&
      this.lastConfig.merchant === config.merchant &&
      this.lastConfig.accessToken === config.accessToken &&
      this.lastConfig.userId === config.userId
    ) {
      return false;
    }

    this.initializing = true;
    try {
      await initialize({
        provider: 'ADYEN',
        store: config.store,
        merchant: config.merchant,
        accessToken: config.accessToken,
        userId: config.userId,
        terminalId: config.terminalId,
      });
      this.initialized = true;
      this.lastConfig = config;
      return true;
    } catch (error) {
      this.initialized = false;
      this.lastConfig = null;
      throw error;
    } finally {
      this.initializing = false;
    }
  }

  warmup(): Promise<void> {
    if (Platform.OS !== 'android') return Promise.resolve();
    if (!this.initialized || this.paymentInProgress) return Promise.resolve();
    if (!this.warmupInFlight) {
      this.warmupInFlight = this.runWarmup().finally(() => {
        this.warmupInFlight = null;
      });
    }
    return this.warmupInFlight;
  }

  private async runWarmup(): Promise<void> {
    let errorMessage: string | undefined;
    try {
      const result = await warmup();
      if (!result?.success) {
        errorMessage = result?.errorMessage ?? 'warm-up returned no result';
      }
    } catch (error: unknown) {
      errorMessage = error instanceof Error ? error.message : String(error);
    }
    if (errorMessage === undefined) return;
    Sentry.addBreadcrumb({
      category: 'tap2pay-embedded',
      message: 'warmup() failed (best-effort, ignored)',
      level: 'warning',
      data: { error: errorMessage },
    });
    logCdsEvent({
      actionType: 'T2P_WARMUP_FAILED',
      tag: AuditLogTags.PAYMENT,
      phrase: 'card reader warm-up failed',
      level: 'warn',
      context: { errorMessage },
    });
  }

  async setDisplaySettings(
    settings: DisplaySettings,
  ): Promise<{ success: boolean }> {
    if (Platform.OS !== 'android') return { success: false };
    return sdkSetDisplaySettings(settings);
  }

  async abortTransaction(): Promise<void> {
    if (Platform.OS !== 'android') return;
    if (!this.paymentInProgress) return;
    try {
      await sdkAbortTransaction();
    } catch (error: unknown) {
      // best-effort
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'abortTransaction() failed (best-effort, ignored)',
        level: 'warning',
        data: { error: String(error) },
      });
    } finally {
      this.paymentInProgress = false;
      this.paymentEndedAt = Date.now();
    }
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  get isPaymentInProgress(): boolean {
    return this.paymentInProgress;
  }

  async pay(params: {
    amount: string;
    currency: string;
    merchantReference?: string;
    tipAmount?: string;
  }): Promise<PaymentResult> {
    if (Platform.OS !== 'android') {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'pay() rejected: not Android',
        level: 'warning',
      });
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'Tap2Pay Embedded is only available on Android',
      };
    }

    if (!this.initialized) {
      Sentry.captureException(
        new Error('Tap2Pay Embedded pay() rejected: SDK not initialized'),
        {
          extra: {
            merchantReference: params.merchantReference,
            context: 'tap2pay-embedded-pay-not-initialized-cds',
          },
        },
      );
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'SDK not initialized. Call initialize() first.',
      };
    }

    if (this.paymentInProgress) {
      Sentry.captureException(
        new Error(
          'Tap2Pay Embedded pay() rejected: payment already in progress',
        ),
        {
          extra: {
            merchantReference: params.merchantReference,
            context: 'tap2pay-embedded-pay-already-in-progress-cds',
          },
        },
      );
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'A payment is already in progress.',
      };
    }

    this.paymentInProgress = true;
    try {
      const terminalId = await this.savedVirtualTerminalId();
      const result = await initiatePayment({
        amount: params.amount,
        currency: params.currency,
        merchantReference: params.merchantReference,
        tipAmount: params.tipAmount,
        terminalId,
      });
      return result;
    } finally {
      this.paymentInProgress = false;
      this.paymentEndedAt = Date.now();
      void this.warmup();
    }
  }
}

export const tap2PayEmbeddedManager = new Tap2PayEmbeddedManager();
